import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import OTPInput from './OTPInput';
import * as Icons from 'react-icons/fa';
import LoadingSpinner from './LoadingSpinner';
import otpService from '../services/otpService';

const FaCheckCircle = Icons.FaCheckCircle as any;
const FaTimesCircle = Icons.FaTimesCircle as any;
const FaSpinner = Icons.FaSpinner as any;
const FaMobileAlt = Icons.FaMobileAlt as any;

const Overlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 450px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const IconContainer = styled.div<{ variant?: 'primary' | 'success' | 'error' }>`
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    if (props.variant === 'success') return props.theme.success + '20';
    if (props.variant === 'error') return props.theme.error + '20';
    return props.theme.primary + '20';
  }};

  svg {
    font-size: 2rem;
    color: ${props => {
      if (props.variant === 'success') return props.theme.success;
      if (props.variant === 'error') return props.theme.error;
      return props.theme.primary;
    }};
  }
`;

const Title = styled.h2`
  color: ${props => props.theme.textPrimary};
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9rem;
  line-height: 1.5;
`;

const PhoneDisplay = styled.div`
  background: ${props => props.theme.background};
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  margin: 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary'; fullWidth?: boolean }>`
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  ${props => props.variant === 'secondary' ? `
    background: white;
    color: ${props.theme.textPrimary};
    border: 2px solid ${props.theme.border};

    &:hover:not(:disabled) {
      background: ${props.theme.surface};
      border-color: ${props.theme.primary};
    }
  ` : `
    background: ${props.theme.primary};
    color: white;

    &:hover:not(:disabled) {
      background: ${props.theme.primaryDark};
      transform: translateY(-2px);
      box-shadow: 0 4px 12px ${props.theme.primary}40;
    }
  `}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ErrorMessage = styled.div`
  background: ${props => props.theme.error}10;
  border: 1px solid ${props => props.theme.error}40;
  color: ${props => props.theme.error};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-size: 0.9rem;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: ${props => props.theme.success}10;
  border: 1px solid ${props => props.theme.success}40;
  color: ${props => props.theme.success};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-size: 0.9rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ResendText = styled.div`
  text-align: center;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: ${props => props.theme.textSecondary};
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin-left: 0.25rem;

  &:hover {
    color: ${props => props.theme.primaryDark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface OTPValidationModalProps {
  isVisible: boolean;
  phoneNumber: string;
  onSuccess: () => void;
  onCancel: () => void;
  customerCode?: string; // Customer code for OTP API
}

const OTPValidationModal: React.FC<OTPValidationModalProps> = ({
  isVisible,
  phoneNumber,
  onSuccess,
  onCancel,
  customerCode = 'CUST001' // Default customer code if not provided
}) => {
  const [step, setStep] = useState<'init' | 'otp' | 'success'>('init');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Format phone number for display (preserve original country code or use Philippines as default)
  const cleanPhoneNumber = phoneNumber.replace(/^\+/, '').trim();

  // Check if phone number already has a country code
  // If it starts with digits and has country code format, preserve it
  // Otherwise, assume Philippines (+63) for backward compatibility
  let formattedPhone = phoneNumber;
  if (!phoneNumber.startsWith('+')) {
    // No country code prefix, add +63 for Philippines (default)
    formattedPhone = `+63${cleanPhoneNumber}`;
  } else {
    // Already has + prefix, keep as is
    formattedPhone = phoneNumber;
  }

  // Reset state when phone number changes
  useEffect(() => {
    if (isVisible) {
      setStep('init');
      setOtp('');
      setError('');
      setResendTimer(0);
    }
  }, [phoneNumber, isVisible]);

  // Resend timer countdown
  useEffect(() => {
    if (!isVisible) return; /* Don't run timer if modal is closed */

    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        if (isVisible) { /* Only update if modal is still open */
          setResendTimer(prev => prev - 1);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer, isVisible]);

  const sendOTP = async () => {
    setLoading(true);
    setError('');

    try {
      // Call new OTP API
      const result = await otpService.generateOTP(customerCode, cleanPhoneNumber);

      if (result.success) {
        setStep('otp');
        setResendTimer(60); // 60 seconds cooldown
      } else {
        throw new Error(result.message || 'Failed to send OTP');
      }
    } catch (err: any) {

      let errorMessage = err.message || 'Failed to send OTP. Please try again.';

      // Handle specific error messages
      if (errorMessage.includes('wait at least')) {
        // Already has a clear message about cooldown
      } else if (errorMessage.includes('Invalid phone')) {
        errorMessage = 'Invalid phone number format.';
      } else if (errorMessage.includes('required')) {
        errorMessage = 'Phone number is required.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call new OTP API
      const result = await otpService.validateOTP(customerCode, cleanPhoneNumber, otp);

      if (result.success && result.isValid) {
        setStep('success');

        // Wait 1.5 seconds to show success message, then proceed
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        // Handle specific validation statuses
        let errorMessage = result.message || 'Invalid OTP. Please try again.';

        if (result.status === 'ALREADY_USED') {
          errorMessage = 'This OTP has already been used. Please request a new one.';
        } else if (result.status === 'EXPIRED') {
          errorMessage = 'OTP has expired. Please request a new one.';
        } else if (result.status === 'MAX_ATTEMPTS_EXCEEDED') {
          errorMessage = 'Maximum validation attempts exceeded. Please request a new OTP.';
        } else if (result.status === 'INVALID_CODE') {
          errorMessage = result.message; // Use the message from API which includes remaining attempts
        } else if (result.status === 'NOT_FOUND') {
          errorMessage = 'No OTP found. Please request a new OTP.';
        }

        setError(errorMessage);
        setOtp(''); // Clear OTP on error
      }
    } catch (err: any) {
      console.error('❌ Verify OTP error:', err);

      setError(err.message || 'Failed to verify OTP. Please try again.');
      setOtp(''); // Clear OTP on error
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setOtp('');
    setError('');
    await sendOTP();
  };

  const handleCancel = () => {
    setStep('init');
    setOtp('');
    setError('');
    onCancel();
  };

  return (
    <Overlay isVisible={isVisible}>
      {loading && <LoadingSpinner isVisible={loading} />}

      <Modal>
        {step === 'init' && (
          <>
            <Header>
              <IconContainer>
                <FaMobileAlt />
              </IconContainer>
              <Title>Verify Phone Number</Title>
              <Subtitle>
                We'll send a verification code to your mobile number
              </Subtitle>
            </Header>

            <PhoneDisplay>{formattedPhone}</PhoneDisplay>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonGroup>
              <Button variant="secondary" onClick={handleCancel} fullWidth>
                Cancel
              </Button>
              <Button onClick={sendOTP} disabled={loading} fullWidth>
                {loading ? <FaSpinner className="fa-spin" /> : 'Get OTP'}
              </Button>
            </ButtonGroup>
          </>
        )}

        {step === 'otp' && (
          <>
            <Header>
              <IconContainer>
                <FaMobileAlt />
              </IconContainer>
              <Title>Enter OTP</Title>
              <Subtitle>
                Enter the 6-digit code sent to {formattedPhone}
              </Subtitle>
            </Header>

            <OTPInput
              length={6}
              value={otp}
              onChange={setOtp}
              onComplete={verifyOTP}
              disabled={loading}
              hasError={!!error}
              autoFocus
            />

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonGroup>
              <Button variant="secondary" onClick={handleCancel} fullWidth disabled={loading}>
                Cancel
              </Button>
              <Button onClick={verifyOTP} disabled={loading || otp.length !== 6} fullWidth>
                {loading ? <FaSpinner className="fa-spin" /> : 'Verify OTP'}
              </Button>
            </ButtonGroup>

            <ResendText>
              Didn't receive code?
              <ResendLink onClick={handleResend} disabled={resendTimer > 0 || loading}>
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
              </ResendLink>
            </ResendText>
          </>
        )}

        {step === 'success' && (
          <>
            <Header>
              <IconContainer variant="success">
                <FaCheckCircle />
              </IconContainer>
              <Title>Verified!</Title>
              <Subtitle>Your phone number has been verified successfully</Subtitle>
            </Header>

            <SuccessMessage>
              <FaCheckCircle />
              Proceeding with form submission...
            </SuccessMessage>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

export default OTPValidationModal;
